import { PIAPIVersion } from "@/apiConfig";
import { subscribeAlmStatusSafely } from "@/backend/almStatusEvents";
import { createRpcBackend } from "@/backend/backendProvider";
import { createEventSubscriber } from "@/backend/eventSubscriberProvider";
import { getCurrentRoute } from "@/router";
import { useAppStore } from "@/stores";
import { getALMBase } from "@/utils/routes";
import { runFnToCall } from "@/utils/serverCaller";

import { almHandlerAc } from "./almHandlerAc";
import { almHandlerAdo } from "./almHandlerAdo";
import { almHandlerCommon } from "./almHandlerCommon";
import { almHandlerJira } from "./almHandlerJira";
import { RemoteCaller } from "./remoteCaller";

// The same factory `$almHandler` exposes, callable outside the Options API -
// `<script setup>` components have no `this` to reach globalProperties through.
export function createAlmHandler(sessionInfo) {
  return runFnToCall((session) => {
    const appStore = useAppStore();
    const type = sessionInfo.alm_connection_type || sessionInfo.almTool;
    const connectionId = sessionInfo.alm_connection_id || sessionInfo.selectedConnectionID;
    const almSessionId = sessionInfo.sync_session_id;
    const sessionBase = getALMBase() + ".session." + almSessionId + ".";

    // Transport-aware backend: common connection ops route through it
    // (NATS when flag + adapter allow, WAMP otherwise). The per-type
    // handlers + WAMP-only common ops keep the `alm.{type}` RemoteCaller.
    // Keep the runFnToCall-provided `session`, but pull the NATS connection
    // and user off the store so the backend can switch transports.
    const context = {
      company: appStore.company,
      session: () => session,
      natsConnection: appStore.natsconnection,
      natsScheme: appStore.natsScheme,
      user: appStore.user,
      route: getCurrentRoute(),
    };
    const backend = createRpcBackend(context);
    const remote = new RemoteCaller(context, "piplanning", PIAPIVersion, "alm." + type);
    return Object.assign(
      almHandlerCommon(backend, type, remote, connectionId, almSessionId),
      {
        onStatus: async (handler) => {
          // pialmsync publishes the status over both transports (like piserver's
          // pubsub events), so listening on both means the handler fires twice
          // per change. Setting a status is idempotent, and it keeps this page
          // live even when the NATS grant or connection is missing.
          const subscriber = almSessionId ? createEventSubscriber(context) : null;
          const nats = subscriber
            ? subscribeAlmStatusSafely(subscriber, almSessionId, (status) => handler([status]))
            : null;
          const wamp = await session.subscribe(sessionBase + "on_status", handler);
          return {
            unsubscribe: async () => {
              await nats?.unsubscribe();
              await wamp?.unsubscribe?.();
            },
          };
        },
      },
      almHandler(type),
    );

    function almHandler(type) {
      switch (type) {
        case "jira":
          return almHandlerJira(remote, connectionId, almSessionId);
        case "ado":
          return almHandlerAdo(remote, connectionId, almSessionId);
        case "ac":
          return almHandlerAc(remote, connectionId, almSessionId);
      }
    }
  });
}

const AlmHandler = {
  install(app) {
    app.config.globalProperties.$almHandler = createAlmHandler;
  },
};

export default AlmHandler;

export function setTakenSprintIDs(iterations, currentTeamID, currentSourceID) {
  const takenSprintIDs = [];
  iterations.forEach((iteration) => {
    iteration.teams.forEach((team) => {
      if (team.id === currentTeamID) {
        team.sources.forEach((source) => {
          if (source.id === currentSourceID) {
            if (source.sprint > 0) {
              takenSprintIDs.push(source.sprint);
            }
          }
        });
        team.takenSprintIDs[currentSourceID] = takenSprintIDs;
      }
    });
  });
}
